import { useEffect, useRef, useState, useCallback } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { BarcodeFormat, DecodeHintType } from "@zxing/library";
import { Camera, Keyboard, Loader2, ScanLine, Zap, ZapOff } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const RETAIL_FORMATS = [
  BarcodeFormat.EAN_13,
  BarcodeFormat.EAN_8,
  BarcodeFormat.UPC_A,
  BarcodeFormat.UPC_E,
  BarcodeFormat.CODE_128,
  BarcodeFormat.CODE_39,
  BarcodeFormat.ITF,
  BarcodeFormat.QR_CODE,
];

const beep = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 1180;
    gain.gain.setValueAtTime(0.09, ctx.currentTime);
    osc.start();
    osc.stop(ctx.currentTime + 0.07);
    setTimeout(() => ctx.close(), 300);
  } catch {
    // a silent till is not a broken till
  }
};

/**
 * Camera barcode scanning that works everywhere: the platform BarcodeDetector where the browser
 * has it (fastest), ZXing as the fallback so iOS Safari, Firefox and desktops scan too.
 */
export const BarcodeScannerDialog = ({
  open,
  onOpenChange,
  onDetected,
  title = "Scan a barcode",
  description = "Hold the barcode inside the frame",
  continuous = false,
}) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const readerRef = useRef(null);
  const lastCodeRef = useRef({ code: "", at: 0 });
  const [starting, setStarting] = useState(true);
  const [error, setError] = useState("");
  const [manual, setManual] = useState("");
  const [torchOn, setTorchOn] = useState(false);
  const [torchable, setTorchable] = useState(false);

  const emit = useCallback(
    (code) => {
      const clean = (code || "").trim();
      if (!clean) return;
      const now = Date.now();
      // The same label lingers in frame — ignore repeats for a moment
      if (lastCodeRef.current.code === clean && now - lastCodeRef.current.at < 1500) return;
      lastCodeRef.current = { code: clean, at: now };

      beep();
      if (navigator.vibrate) navigator.vibrate(35);
      window.HostPointNative?.scanFeedback?.();
      onDetected(clean);
      if (!continuous) onOpenChange(false);
    },
    [continuous, onDetected, onOpenChange]
  );

  const stopCamera = useCallback(() => {
    try {
      readerRef.current?.reset?.();
    } catch {
      // reader already torn down
    }
    readerRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    setStarting(true);
    setError("");
    setTorchOn(false);

    (async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setError("This device has no camera the browser can use. Type the number instead.");
        setStarting(false);
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 } },
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        const track = stream.getVideoTracks()[0];
        setTorchable(Boolean(track?.getCapabilities?.().torch));
        setStarting(false);

        if ("BarcodeDetector" in window) {
          // eslint-disable-next-line no-undef
          const detector = new BarcodeDetector({
            formats: ["ean_13", "ean_8", "upc_a", "upc_e", "code_128", "code_39", "itf", "qr_code"],
          });
          const tick = async () => {
            if (cancelled || !videoRef.current) return;
            try {
              const hits = await detector.detect(videoRef.current);
              if (hits.length > 0) emit(hits[0].rawValue);
            } catch {
              // a bad frame is not a failure
            }
            if (!cancelled) setTimeout(tick, 250);
          };
          tick();
          return;
        }

        const hints = new Map();
        hints.set(DecodeHintType.POSSIBLE_FORMATS, RETAIL_FORMATS);
        hints.set(DecodeHintType.TRY_HARDER, true);
        const reader = new BrowserMultiFormatReader(hints, { delayBetweenScanAttempts: 200 });
        readerRef.current = reader;
        reader.decodeFromStream(stream, videoRef.current, (result) => {
          if (result && !cancelled) emit(result.getText());
        });
      } catch (e) {
        if (!cancelled) {
          setError(
            e?.name === "NotAllowedError"
              ? "Camera access was blocked. Allow the camera, or type the number instead."
              : "The camera could not be started. Type the number instead."
          );
          setStarting(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      stopCamera();
    };
  }, [open, emit, stopCamera]);

  const toggleTorch = async () => {
    const track = streamRef.current?.getVideoTracks?.()[0];
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: !torchOn }] });
      setTorchOn((prev) => !prev);
    } catch {
      setTorchable(false);
    }
  };

  const submitManual = (e) => {
    e.preventDefault();
    const code = manual.trim();
    if (!code) return;
    setManual("");
    emit(code);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="barcode-scanner-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScanLine className="h-5 w-5 text-primary" />
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="relative overflow-hidden rounded-xl bg-black aspect-[4/3]">
          <video
            ref={videoRef}
            className="h-full w-full object-cover"
            muted
            playsInline
            data-testid="scanner-video"
          />
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="h-24 w-4/5 rounded-lg border-2 border-emerald-400/90 shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]" />
          </div>
          {starting && !error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/70 text-white">
              <Loader2 className="h-6 w-6 animate-spin" />
              <p className="text-sm">Starting the camera…</p>
            </div>
          )}
          {error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/80 p-6 text-center text-white">
              <Camera className="h-6 w-6" />
              <p className="text-sm" data-testid="scanner-error">{error}</p>
            </div>
          )}
          {torchable && !error && (
            <Button
              type="button"
              size="icon"
              variant="secondary"
              className="absolute right-3 top-3 h-9 w-9 rounded-full"
              onClick={toggleTorch}
              data-testid="scanner-torch-btn"
            >
              {torchOn ? <ZapOff className="h-4 w-4" /> : <Zap className="h-4 w-4" />}
            </Button>
          )}
        </div>

        <form onSubmit={submitManual} className="flex gap-2">
          <div className="relative flex-1">
            <Keyboard className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={manual}
              onChange={(e) => setManual(e.target.value)}
              placeholder="Or type the barcode"
              className="pl-9"
              inputMode="numeric"
              data-testid="scanner-manual-input"
            />
          </div>
          <Button type="submit" variant="outline" data-testid="scanner-manual-submit">
            Use
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Hardware scan guns: USB/Bluetooth ones type like a keyboard, while rugged Android tills
 * broadcast an intent that the native shell forwards as `hostpoint:scan`.
 */
export const useHardwareScan = (onScan, enabled = true) => {
  useEffect(() => {
    if (!enabled) return undefined;
    const handler = (event) => {
      const code = event.detail?.code;
      if (code) onScan(code);
    };
    window.addEventListener("hostpoint:scan", handler);
    return () => window.removeEventListener("hostpoint:scan", handler);
  }, [onScan, enabled]);
};
